import { useState } from 'react';
import { NavLink, Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import FuturizerLogo from './FuturizerLogo';
import { ChangePasswordModal } from './ProjectSelectPage';

const WORKSPACE_TABS = [
  { tab: 'source',  icon: '📂', label: 'Source'  },
  { tab: 'tasks',   icon: '⚡', label: 'Tasks'   },
  { tab: 'results', icon: '📊', label: 'Results' },
];

const OTHER_NAV_ITEMS = [
  { to: '/app/standards', icon: '📋', label: 'Standards' },
];

export default function AppShell() {
  const { user, project, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen,   setSidebarOpen]   = useState(true);
  const [changePwdOpen, setChangePwdOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  function isWorkspaceTabActive(tab) {
    if (location.pathname !== '/app/workspace') return false;
    const currentTab = new URLSearchParams(location.search).get('tab');
    return currentTab === tab || (!currentTab && tab === 'source');
  }

  function handleLogout() {
    logout();
    navigate('/login', { replace: true });
  }

  function handleChangeProject() {
    navigate('/projects');
  }

  return (
    <div className="app-shell">
      {/* ── Mobile sidebar overlay ────────────────────────── */}
      <div
        className={`sidebar-overlay${mobileMenuOpen ? ' sidebar-overlay--visible' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* ── Sidebar ──────────────────────────────────────── */}
      <aside className={`app-sidebar${sidebarOpen ? '' : ' app-sidebar--collapsed'}${mobileMenuOpen ? ' app-sidebar--mobile-open' : ''}`}>
        <div className="app-heading" style={{ justifyContent: sidebarOpen ? 'space-between' : 'center' }}>
          {sidebarOpen && <FuturizerLogo size={32} variant="white-full" />}
          <button
            className="sidebar-toggle"
            style={{ position: 'static', transform: 'none' }}
            onClick={() => setSidebarOpen(o => !o)}
            title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {sidebarOpen ? '‹' : '›'}
          </button>
        </div>

        <nav className="app-nav">
          {WORKSPACE_TABS.map(({ tab, icon, label }) => (
            <Link
              key={tab}
              to={`/app/workspace?tab=${tab}`}
              className={`nav-link${isWorkspaceTabActive(tab) ? ' active' : ''}`}
            >
              <span className="nav-icon">{icon}</span>
              <span className="nav-label">{label}</span>
            </Link>
          ))}
          {OTHER_NAV_ITEMS.map(({ to, icon, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
            >
              <span className="nav-icon">{icon}</span>
              <span className="nav-label">{label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Footer: project + user + actions */}
        <div style={styles.sidebarFooter}>
          {sidebarOpen ? (
            <>
              {/* Project badge — clickable to change */}
              <button style={styles.projectBadge} onClick={handleChangeProject} title="Change project">
                <span style={styles.projectIcon}>📁</span>
                <div style={styles.projectInfo}>
                  <span style={styles.projectName}>{project?.name}</span>
                  <span style={styles.projectId}>ID #{project?.id}</span>
                </div>
                <span style={styles.changeIcon} title="Switch project">⇄</span>
              </button>

              {/* User row */}
              <div style={styles.userRow}>
                <div style={styles.avatar}>
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
                <div style={styles.userInfo}>
                  <span style={styles.userName}>{user?.name}</span>
                  <span style={styles.userEmail}>{user?.email}</span>
                </div>
              </div>

              {/* Action buttons */}
              <div style={styles.actionRow}>
                <button style={styles.changeProjectBtn} onClick={handleChangeProject}>
                  ⇄ Change Project
                </button>
                <button style={styles.logoutBtn} onClick={handleLogout}>
                  ↩ Logout
                </button>
              </div>
              <button style={{ ...styles.logoutBtn, width: '100%', marginTop: 8 }} onClick={() => setChangePwdOpen(true)}>
                🔒 Change Password
              </button>
            </>
          ) : (
            /* Collapsed — avatar only */
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div style={styles.avatar} title={user?.name}>
                {user?.name?.charAt(0).toUpperCase()}
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* ── Main content ─────────────────────────────────── */}
      <div className="app-content">
        {/* Top bar — user + project + change button */}
        <div style={styles.topBar}>
          {/* Hamburger — visible on mobile only */}
          <button className="hamburger-btn" onClick={() => setMobileMenuOpen(o => !o)} aria-label="Open menu">
            ☰
          </button>
          <div style={styles.topBarLeft} className="top-bar-left">
            <span style={styles.topBarItem}>
              <span style={styles.topBarIcon}>📁</span>
              <span style={styles.topBarLabel}>Project</span>
              <span style={styles.topBarValue}>{project?.name}</span>
            </span>
          </div>

          {/* Change project — top bar shortcut */}
          <button style={styles.topBarChangeBtn} onClick={handleChangeProject}>
            ⇄ Change Project
          </button>
        </div>

        <Outlet />
      </div>

      {changePwdOpen && (
        <ChangePasswordModal user={user} onClose={() => setChangePwdOpen(false)} onPasswordChanged={handleLogout} />
      )}
    </div>
  );
}

const styles = {
  /* ── Sidebar footer ──────────────────────────────────── */
  sidebarFooter: {
    padding: '12px 14px 20px',
    borderTop: '1px solid rgba(255,255,255,0.1)',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },

  projectBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 8,
    padding: '8px 10px',
    cursor: 'pointer',
    width: '100%',
    transition: 'background 0.15s',
    textAlign: 'left',
  },
  projectIcon: { fontSize: 16, flexShrink: 0 },
  projectInfo: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 },
  projectName: {
    fontSize: 12, fontWeight: 700, color: '#fff',
    lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  projectId: { fontSize: 10, color: 'rgba(255,255,255,0.5)', lineHeight: 1.3 },
  changeIcon: {
    fontSize: 14, color: 'rgba(255,255,255,0.5)',
    flexShrink: 0, transition: 'color 0.15s',
  },

  userRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0' },
  avatar: {
    width: 30, height: 30, borderRadius: '50%',
    background: 'linear-gradient(135deg,#E65100,#F4511E)',
    border: '2px solid rgba(255,255,255,0.25)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 13, fontWeight: 700, color: '#fff', flexShrink: 0,
  },
  userInfo: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  userName: {
    fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.9)',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  userEmail: {
    fontSize: 10, color: 'rgba(255,255,255,0.45)',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },

  actionRow: { display: 'flex', gap: 10 },
  changeProjectBtn: {
    flex: 1,
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.18)',
    borderRadius: 7, color: 'rgba(255,255,255,0.8)',
    fontSize: 11, fontWeight: 600, padding: '9px 12px',
    cursor: 'pointer', transition: 'background 0.15s',
    whiteSpace: 'nowrap',
  },
  logoutBtn: {
    flex: 1,
    background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(255,255,255,0.14)',
    borderRadius: 7, color: 'rgba(255,255,255,0.7)',
    fontSize: 11, fontWeight: 600, padding: '9px 12px',
    cursor: 'pointer', transition: 'background 0.15s',
    whiteSpace: 'nowrap',
  },

  /* ── Top bar ─────────────────────────────────────────── */
  topBar: {
    background: 'var(--bg-card)',
    borderBottom: '1px solid var(--border)',
    padding: '7px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxShadow: 'var(--shadow-xs)',
    flexShrink: 0,
  },
  topBarLeft: { display: 'flex', alignItems: 'center', gap: 12 },
  topBarItem: { display: 'flex', alignItems: 'center', gap: 6 },
  topBarIcon: { fontSize: 13 },
  topBarLabel: {
    fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '0.7px', color: 'var(--text-muted)',
  },
  topBarValue: { fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' },
  topBarSep: { color: 'var(--border-strong)', fontWeight: 300, fontSize: 16, lineHeight: 1 },
  clockBox: {
    display: 'flex', flexDirection: 'column', alignItems: 'flex-end',
    padding: '4px 14px', borderRadius: 'var(--radius-sm)',
    background: 'var(--bg-table-row-alt)', border: '1px solid var(--border)',
  },
  clockTime: {
    fontSize: 16, fontWeight: 700, color: 'var(--text-primary)',
    fontVariantNumeric: 'tabular-nums', lineHeight: 1.2,
    fontFamily: 'var(--font-mono, monospace)',
  },
  clockDate: {
    fontSize: 10, fontWeight: 500, color: 'var(--text-muted)',
    letterSpacing: '0.3px', lineHeight: 1.3,
  },

  topBarChangeBtn: {
    display: 'flex', alignItems: 'center', gap: 6,
    background: 'var(--info-bg)',
    border: '1px solid rgba(21,101,192,0.2)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--primary)',
    fontSize: 12, fontWeight: 600,
    padding: '5px 12px', cursor: 'pointer',
    transition: 'background 0.15s',
    whiteSpace: 'nowrap',
  },
};
